import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Input, InputRef, Space, Table, TableColumnType } from "antd";
import { CustomerProps } from "../../interfaces/interfaces";
import { SearchOutlined } from "@ant-design/icons";
import DataContext from "../../context/DataContext";
import { ColumnsType } from "antd/es/table";
import { TablePaginationConfig } from "antd/lib";
import { FilterDropdownProps, FilterValue } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";

interface Props {
  customersSelected: string[];
  setCustomerList: (customers: React.Key[]) => void;
}

type DataIndex = keyof CustomerProps;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const CustomerListWithNoDetails = ({
  customersSelected,
  setCustomerList,
}: Props) => {
  const [tableParams, setTableParams] = useState<TableParams>({});
  const { customerByClient } = useContext(DataContext);
  const [selectedRowsToEdit, setSelectedRowToEdit] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  useEffect(() => {
    setSelectedRowToEdit((prevSelected) => {
      // Only update if there's a true change in the prop
      const propChanged =
        customersSelected.length !== prevSelected.length ||
        !customersSelected.every((id) => prevSelected.includes(id));

      return propChanged ? customersSelected : prevSelected;
    });
  }, [customersSelected]);

  useEffect(() => {
    if (!searchText) {
      setTableParams({
        pagination: {
          pageSize: 100,
          total: customerByClient.length,
          onChange: (_, pageSize) => {
            setTableParams((prev) => {
              return {
                ...prev,
                pagination: {
                  ...prev.pagination,
                  pageSize: pageSize,
                },
              };
            });
          },
        },
      });
    }
  }, [customerByClient, searchText]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (
    clearFilters: () => void,
    confirm: FilterDropdownProps["confirm"],
  ) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): TableColumnType<CustomerProps> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={"Search Customer"}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{
          color: "white",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#17549a")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]!.toString()
            .toLowerCase()
            .includes((value as string).toLowerCase())
        : false,
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#FFF", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<CustomerProps> = [
    {
      title: "Customers",
      dataIndex: "DisplayName",
      key: "DisplayName",
      className: "table-row",
      sorter: {
        compare: (a, b) => a.DisplayName.localeCompare(b.DisplayName),
        multiple: 1,
      },
      ellipsis: {
        showTitle: false,
      },
      ...getColumnSearchProps("DisplayName"),
    },
  ];

  const onChangeSelectedRowsToEdit = (newSelectedRowToEdit: React.Key[]) => {
    setCustomerList(newSelectedRowToEdit);
    requestAnimationFrame(() => {
      setSelectedRowToEdit(newSelectedRowToEdit);
    });
  };

  const rowSelectionToEdit = {
    selectedRowKeys: selectedRowsToEdit,
    onChange: onChangeSelectedRowsToEdit,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  return (
    <>
      <Table<CustomerProps>
        columns={columns}
        rowSelection={rowSelectionToEdit}
        dataSource={customerByClient}
        pagination={tableParams.pagination}
        size="small"
        rowKey={"_id"}
        scroll={{ y: "70vh", x: "100%" }}
      />
    </>
  );
};
